import { useCallback, useEffect, useRef, useState } from 'react';
import companyJourneyService, {
  subscribeToJourneyChanges,
} from '../services/companyJourneyService';

/**
 * This account's activation journey.
 *
 * A failed read reports `known: false` with empty shapes rather than asserting
 * an empty board. The two are different answers and the surfaces route on the
 * difference: nav disclosure shows everything on unknown, the billing section
 * hides on it. A caller that reads `premium.eligible` without checking `known`
 * first is reading a null it cannot interpret.
 *
 * `refetch` is stable for the life of the mount, so it can be handed to an
 * effect dependency array without re-arming it on every render.
 */
const emptyJourney = () => ({
  chapters: [],
  sideQuests: [],
  nextStep: null,
  premium: { eligible: null, sellable: false },
  known: false,
  loading: false,
});

const useCompanyJourney = (token) => {
  const [state, setState] = useState(() => ({
    ...emptyJourney(),
    loading: !!token,
  }));
  const mounted = useRef(true);
  // The token is read through a ref so `load` can keep an empty dependency
  // array and therefore a stable identity. The effect below still re-runs on a
  // token change, which is what actually refetches.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const load = useCallback(async () => {
    if (!tokenRef.current) {
      if (mounted.current) setState(emptyJourney());
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const payload = await companyJourneyService.getJourney();
      if (!mounted.current) return;
      if (!payload || typeof payload !== 'object') {
        // A 200 we cannot read is not a board with nothing on it.
        setState(emptyJourney());
        return;
      }
      setState({
        chapters: Array.isArray(payload.chapters) ? payload.chapters : [],
        sideQuests: Array.isArray(payload.sideQuests) ? payload.sideQuests : [],
        nextStep: payload.nextStep ?? null,
        premium: {
          eligible:
            typeof payload.premium?.eligible === 'boolean'
              ? payload.premium.eligible
              : null,
          sellable: payload.premium?.sellable === true,
        },
        known: true,
        loading: false,
      });
    } catch {
      if (!mounted.current) return;
      setState(emptyJourney());
    }
  }, []);

  useEffect(() => {
    // Re-arm in SETUP, not only in cleanup. StrictMode double-invokes mount
    // effects in dev, and a flag only ever set false discards every later
    // response, leaving a board that never leaves its loading state.
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [token, load]);

  useEffect(() => {
    // Two ways the board learns it is stale. The signal is this tab's own
    // writes; focus covers everything that happened while the tab was not the
    // one being looked at, including the other side of a negotiation.
    //
    // Both go through `load`, which no-ops without a token, so neither can turn
    // a signed-out tab into a request.
    const unsubscribe = subscribeToJourneyChanges(load);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  return { ...state, refetch: load };
};

export default useCompanyJourney;
